// Web Audio API Sound Synthesizer & Physical Audio Player for Casino SFX

class SoundManager {
  constructor() {
    this.audioCtx = null;
    this.soundEnabled = true;
    this.wheelSpinAudio = null;
    this.fadeInterval = null;
  }

  initContext() {
    if (!this.audioCtx) {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (AudioContext) {
        this.audioCtx = new AudioContext();
      }
    }
    if (this.audioCtx && this.audioCtx.state === 'suspended') {
      this.audioCtx.resume().catch(() => {});
    }
    return this.audioCtx;
  }

  setEnabled(val = true) {
    this.soundEnabled = Boolean(val);
    if (!this.soundEnabled && this.wheelSpinAudio) {
      try {
        this.wheelSpinAudio.pause();
      } catch (e) {}
    }
  }

  toggleSound() {
    this.setEnabled(!this.soundEnabled);
    return this.soundEnabled;
  }

  // Pre-prime audio elements on initial user interaction
  unlockAudio() {
    this.initContext();
    if (!this.wheelSpinAudio) {
      this.getWheelSpinAudio();
    }
    if (this.wheelSpinAudio) {
      try {
        this.wheelSpinAudio.load();
      } catch (e) {}
    }
  }

  getWheelSpinAudio() {
    if (!this.wheelSpinAudio) {
      const audio = new Audio("/roulette_wheel_spinning.mp3");
      audio.preload = "auto";
      audio.loop = true;
      audio.onerror = () => {
        // Fallback to URI-encoded path if needed
        if (audio.src.indexOf("Roulette%20Wheel%20Spinning.mp3") === -1) {
          audio.src = "/Roulette%20Wheel%20Spinning.mp3";
          audio.load();
        }
      };
      this.wheelSpinAudio = audio;
    }
    return this.wheelSpinAudio;
  }

  // Play the physical Roulette Wheel Spinning MP3 audio ONLY while wheel is spinning
  playWheelSpinAudio() {
    if (!this.soundEnabled) return;
    this.isSpinning = true;
    try {
      const audio = this.getWheelSpinAudio();
      audio.loop = true;
      audio.volume = 1.0;
      audio.currentTime = 0;
      const playPromise = audio.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            // Guard: If wheel already stopped while play() was in-flight, immediately pause and reset
            if (!this.isSpinning) {
              audio.loop = false;
              audio.pause();
              audio.currentTime = 0;
            }
          })
          .catch((err) => {
            console.warn("Wheel spin MP3 play prevented by browser:", err);
          });
      }
    } catch (e) {
      console.warn("playWheelSpinAudio error:", e);
    }
  }

  // Instantly STOP wheel spin MP3 when wheel stops spinning
  stopWheelSpinAudio() {
    this.isSpinning = false;
    if (this.wheelSpinAudio) {
      try {
        this.wheelSpinAudio.loop = false;
        this.wheelSpinAudio.pause();
        this.wheelSpinAudio.currentTime = 0;
      } catch (e) {}
    }
  }

  // Wheel spinning strictly uses the MP3 file - no synthetic ticks
  playSpinTickSound() {
    // Safe no-op: pure MP3 wheel spin sound is used instead
  }

  // Crisp, loud arcade countdown BEEP for the last 10 seconds (10 to 1)
  playCountdownSound(secondsRemaining) {
    if (!this.soundEnabled) return;
    try {
      const ctx = this.initContext();
      if (!ctx) return;

      const now = ctx.currentTime;
      const isUrgent = secondsRemaining !== undefined && secondsRemaining <= 3;
      const primaryFreq = isUrgent ? 1350 : 920; // 920Hz electronic beep, 1350Hz urgent warning
      const duration = isUrgent ? 0.16 : 0.10;
      const volume = isUrgent ? 0.60 : 0.48;

      // Primary tone: clean, resonant sine
      const osc1 = ctx.createOscillator();
      const gain1 = ctx.createGain();
      osc1.type = 'sine';
      osc1.frequency.setValueAtTime(primaryFreq, now);

      gain1.gain.setValueAtTime(0.0001, now);
      gain1.gain.linearRampToValueAtTime(volume, now + 0.005);
      gain1.gain.exponentialRampToValueAtTime(0.0001, now + duration);

      osc1.connect(gain1);
      gain1.connect(ctx.destination);

      osc1.start(now);
      osc1.stop(now + duration + 0.02);

      // Second harmonic overtone (punchy digital clock / arcade beep presence)
      const osc2 = ctx.createOscillator();
      const gain2 = ctx.createGain();
      osc2.type = 'triangle';
      osc2.frequency.setValueAtTime(primaryFreq * 2, now);

      gain2.gain.setValueAtTime(0.0001, now);
      gain2.gain.linearRampToValueAtTime(volume * 0.35, now + 0.005);
      gain2.gain.exponentialRampToValueAtTime(0.0001, now + (duration * 0.7));

      osc2.connect(gain2);
      gain2.connect(ctx.destination);

      osc2.start(now);
      osc2.stop(now + (duration * 0.7) + 0.02);
    } catch (e) {
      console.warn("Countdown audio error:", e);
    }
  }

  // "No More Bets / Time Up" tone when countdown hits 0
  playNoMoreBetsSound() {
    if (!this.soundEnabled) return;
    try {
      const ctx = this.initContext();
      if (!ctx) return;

      const now = ctx.currentTime;
      [660, 440].forEach((freq, idx) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'triangle';
        osc.frequency.setValueAtTime(freq, now + idx * 0.12);

        gain.gain.setValueAtTime(0.28, now + idx * 0.12);
        gain.gain.exponentialRampToValueAtTime(0.001, now + idx * 0.12 + 0.2);

        osc.connect(gain);
        gain.connect(ctx.destination);

        osc.start(now + idx * 0.12);
        osc.stop(now + idx * 0.12 + 0.22);
      });
    } catch (e) {}
  }

  // Clink sound when placing / putting coins - disabled
  playChipSound() {
    // Disabled as requested: no coin put sound
  }

  // Soft ball settle tick
  playBallSettleSound() {
    if (!this.soundEnabled) return;
    try {
      const ctx = this.initContext();
      if (!ctx) return;

      const now = ctx.currentTime;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = 'sine';
      osc.frequency.setValueAtTime(350, now);
      osc.frequency.exponentialRampToValueAtTime(120, now + 0.08);

      gain.gain.setValueAtTime(0.18, now);
      gain.gain.exponentialRampToValueAtTime(0.005, now + 0.08);

      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.start(now);
      osc.stop(now + 0.08);
    } catch (e) {}
  }

  // Victory fanfare on win
  playWinSound() {
    if (!this.soundEnabled) return;
    try {
      const ctx = this.initContext();
      if (!ctx) return;

      const notes = [523.25, 659.25, 783.99, 1046.5]; // C5, E5, G5, C6
      const now = ctx.currentTime;
      notes.forEach((freq, index) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();

        osc.type = 'triangle';
        osc.frequency.value = freq;

        const startTime = now + index * 0.1;
        gain.gain.setValueAtTime(0.25, startTime);
        gain.gain.exponentialRampToValueAtTime(0.001, startTime + 0.25);

        osc.connect(gain);
        gain.connect(ctx.destination);

        osc.start(startTime);
        osc.stop(startTime + 0.25);
      });
    } catch (e) {}
  }
}

const rawSoundManager = new SoundManager();

// Defensive proxy wrapper so any unexpected or legacy sound call never throws
export const sounds = new Proxy(rawSoundManager, {
  get(target, prop) {
    if (prop in target) {
      const val = target[prop];
      if (typeof val === 'function') {
        return val.bind(target);
      }
      return val;
    }
    // Safe no-op for any undefined sound method call
    if (typeof prop === 'string') {
      return () => {};
    }
    return undefined;
  }
});